import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Card, CardContent, Typography, Rating, TextField, Button,
    Paper, Chip, IconButton, Container, Stack, Link,
    Snackbar, Alert, CircularProgress, Grid, Divider
} from '@mui/material';
import {
    PhotoCamera, VideoCall, Send, Star, StarBorder,
    Close, WifiOff, LocalGasStation, Speed, People,
    CheckCircle, LocationOn, CalendarToday, AttachMoney,
    ColorLens, Directions, Build
} from '@mui/icons-material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import Notes from './header';
import { useNavigate, useLocation } from 'react-router-dom';
import FooterManual from './footerManual';

const theme = createTheme({
    palette: {
        primary: { main: '#2563eb' },
        secondary: { main: '#f59e0b' },
        error: { main: '#ef4444' },
        success: { main: '#10b981' },
        warning: { main: '#f59e0b' },
        background: { default: '#f8fafc', paper: '#ffffff' }
    },
    typography: {
        fontFamily: '"Inter", "Roboto", sans-serif',
    },
});

// API Configuration
const API_BASE_URL = 'https://car.amar.co.jp/api/review-rating/review-rating';
const ORDER_HISTORY_API_URL = 'https://car.amar.co.jp/api/authorization/calculate-order-history/get';
const IMAGE_API_BASE_URL = 'https://car.amar.co.jp/api/image';
const FALLBACK_IMAGE = '/carousel/carslider.png';

export default function ProductReviewInterface() {
    const navigate = useNavigate();
    const location = useLocation();

    // State variables
    const [rating, setRating] = useState(0);
    const [review, setReview] = useState('');
    const [reviewTitle, setReviewTitle] = useState('');
    const [mediaFiles, setMediaFiles] = useState([]);
    const [mediaPreviews, setMediaPreviews] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });
    const [userLoggedIn, setUserLoggedIn] = useState(false);
    const [orderId, setOrderId] = useState(null);
    const [carModelId, setCarModelId] = useState(null);
    const [networkStatus, setNetworkStatus] = useState(navigator.onLine);
    const [carDetails, setCarDetails] = useState(null);
    const [orderDetails, setOrderDetails] = useState(null);
    const [carImage, setCarImage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [orderHistory, setOrderHistory] = useState([]);

    // Get authentication token
    const getAuthToken = () => {
        try {
            const token = localStorage.getItem('authToken') ||
                sessionStorage.getItem('authToken');
            return token ? token.trim() : null;
        } catch (error) {
            console.error('Error retrieving token:', error);
            return null;
        }
    };

    // Cleanup media previews on unmount
    useEffect(() => {
        return () => {
            mediaPreviews.forEach((url) => URL.revokeObjectURL(url));
        };
    }, [mediaPreviews]);

    // Extract order and car model IDs from URL
    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        const orderIdParam = urlParams.get('orderId') || localStorage.getItem('currentOrderId');
        const carModelIdParam = urlParams.get('carModelId') || localStorage.getItem('currentCarModelId');

        if (orderIdParam) {
            setOrderId(parseInt(orderIdParam));
        }
        if (carModelIdParam) {
            setCarModelId(parseInt(carModelIdParam));
        }

        // Check login status
        const token = getAuthToken();
        setUserLoggedIn(!!token);

        // Network status listeners
        const handleOnline = () => setNetworkStatus(true);
        const handleOffline = () => setNetworkStatus(false);

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, [location]);

    // Fetch order history and car details
    useEffect(() => {
        if (userLoggedIn) {
            fetchOrderHistory();
        }
    }, [userLoggedIn]);

    // When orderId is set, find the specific order
    useEffect(() => {
        if (orderId && orderHistory.length > 0) {
            const selectedOrder = orderHistory.find(order => order.id === orderId);
            if (selectedOrder) {
                setOrderDetails(selectedOrder);
                setCarModelId(selectedOrder.carModelId);
                fetchCarDetails(selectedOrder);
                fetchCarImage(selectedOrder.carModelId);
            }
        }
    }, [orderId, orderHistory]);

    // Fetch order history
    const fetchOrderHistory = async () => {
        try {
            setLoading(true);
            const token = getAuthToken();

            if (!token) {
                showSnackbar('Please login to view order history', 'error');
                return;
            }

            const response = await fetch(ORDER_HISTORY_API_URL, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            const data = await response.json();

            if (data.success) {
                setOrderHistory(data.data);

                if (!orderId && data.data.length > 0) {
                    const firstOrder = data.data[0];
                    setOrderId(firstOrder.id);
                    setCarModelId(firstOrder.carModelId);
                    setOrderDetails(firstOrder);
                    fetchCarDetails(firstOrder);
                    fetchCarImage(firstOrder.carModelId);
                }
            } else {
                throw new Error(data.message || 'Failed to fetch order history');
            }
        } catch (error) {
            console.error('Error fetching order history:', error);
            showSnackbar('Failed to load order history', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Fetch car details from order data
    const fetchCarDetails = (orderData) => {
        if (!orderData) return;

        const carData = {
            carName: `${orderData.carBrandName} ${orderData.carModelName}`,
            brand: orderData.carBrandName,
            model: orderData.carModelName,
            year: orderData.carRegistrationDate ? orderData.carRegistrationDate.split('-')[0] : 'N/A',
            fuelType: orderData.carFuel,
            transmission: orderData.carTransmission,
            seatingCapacity: orderData.carSeat,
            mileage: `${orderData.carMileage.toLocaleString()} km`,
            color: orderData.carColor,
            engine: `${orderData.carEngineCC}cc`,
            price: `${orderData.carPrice} ${orderData.carPriceCurrency}`,
            carRefNumber: orderData.carRefNumber,
            carRegistrationDate: orderData.carRegistrationDate,
            carManufactureDate: orderData.carManufactureDate,
            carModelCode: orderData.carModelCode,
            carGrade: orderData.carGrade,
            carChassisNumber: orderData.carChassisNumber,
            carEngineCode: orderData.carEngineCode,
            carDoors: orderData.carDoors,
            carWeight: orderData.carWeight,
            carLocation: orderData.carLocation,
            carLength: orderData.carLength,
            carWidth: orderData.carWidth,
            carHeight: orderData.carHeight,
            airConditioning: orderData.airConditioning,
            carDrive: orderData.carDrive,
            carCategoriesName: orderData.carCategoriesName,
            steering: orderData.carSteering,
            volumeCubicMeters: orderData.volumeCubicMeters,
            shippingOption: orderData.shippingOption,
            orderRefId: orderData.orderRefId,
            countryName: orderData.countryName,
            portName: orderData.portName,
            totalCost: orderData.totalCost,
            totalBaseShippingCostAll: orderData.totalBaseShippingCostAll,
            totalCostAllCars: orderData.totalCostAllCars,
            ourPick: orderData.ourPick,
            specialDeal: orderData.specialDeal,
            isOrderPlaced: orderData.isOrderPlaced
        };

        const features = [];
        if (orderData.airConditioning) features.push('Air Conditioning');
        if (orderData.ourPick) features.push('Our Pick');
        if (orderData.specialDeal) features.push('Special Deal');
        if (orderData.carSteering === 'RIGHT') features.push('Right Hand Drive');
        if (orderData.carDrive === '4WD') features.push('4 Wheel Drive');

        carData.features = features;
        setCarDetails(carData);
    };

    // Fetch single car image with fallback check
    const fetchCarImage = (modelId) => {
        if (!modelId) {
            setCarImage(FALLBACK_IMAGE);
            return;
        }
        const imageUrl = `${IMAGE_API_BASE_URL}/car-exterior-images-1/get-image/${modelId}`;
        setCarImage(imageUrl);
    };

    // Show snackbar
    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    // Upload media files
    const uploadMediaFiles = async (ratingId) => {
        if (!mediaFiles.length) return true;

        const token = getAuthToken();
        if (!token) {
            showSnackbar('Authentication required', 'error');
            return false;
        }

        try {
            const formData = new FormData();
            formData.append('orderId', orderId.toString());

            mediaFiles.forEach((file) => {
                formData.append('mediaFiles', file, file.name);
            });

            const response = await fetch(`${API_BASE_URL}/upload-media`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });

            if (!response.ok) throw new Error(`HTTP ${response.status}`);

            const data = await response.json();

            if (data.success) {
                showSnackbar(`${mediaFiles.length} media file(s) uploaded successfully!`, 'success');
                return true;
            } else {
                throw new Error(data.message || 'Media upload failed');
            }
        } catch (error) {
            console.error('Media upload error:', error);
            showSnackbar(`Failed to upload media: ${error.message}`, 'error');
            return false;
        }
    };

    // Submit rating and media
    const submitRating = async () => {
        const token = getAuthToken();

        if (!token) {
            showSnackbar('Please login to submit review', 'error');
            return;
        }

        if (!orderId || !carModelId) {
            showSnackbar('Order information is missing', 'error');
            return;
        }

        if (!rating) {
            showSnackbar('Please select a rating', 'warning');
            return;
        }

        if (!reviewTitle.trim()) {
            showSnackbar('Please enter a review title', 'warning');
            return;
        }

        if (!review.trim()) {
            showSnackbar('Please enter your review', 'warning');
            return;
        }

        try {
            setIsSubmitting(true);

            const ratingData = {
                orderId: orderId,
                carModelId: carModelId,
                rating: rating,
                reviewTitle: reviewTitle.trim(),
                shareYourExperince: review.trim()
            };

            const ratingResponse = await fetch(`${API_BASE_URL}/submit-rating`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(ratingData),
            });

            if (!ratingResponse.ok) throw new Error(`HTTP ${ratingResponse.status}`);

            const ratingDataResponse = await ratingResponse.json();

            if (!ratingDataResponse.success) {
                throw new Error(ratingDataResponse.message || 'Failed to submit review');
            }

            const ratingId = ratingDataResponse.data?.ratingId;
            showSnackbar('Review submitted successfully!', 'success');

            if (mediaFiles.length > 0 && ratingId) {
                const mediaUploadSuccess = await uploadMediaFiles(ratingId);
                if (!mediaUploadSuccess) {
                    console.warn('Media upload failed, but review was submitted');
                }
            }

            // Cleanup previews
            mediaPreviews.forEach((url) => URL.revokeObjectURL(url));

            setSubmitted(true);
            setRating(0);
            setReview('');
            setReviewTitle('');
            setMediaFiles([]);
            setMediaPreviews([]);

            setTimeout(() => setSubmitted(false), 3000);
        } catch (error) {
            console.error('Submit review error:', error);

            let userMessage = error.message;
            let severity = 'error';

            if (error.message.includes('NetworkError') ||
                error.message.includes('Failed to fetch')) {
                userMessage = 'Network error. Please check your connection.';
                severity = networkStatus ? 'error' : 'warning';
            } else if (error.message.includes('401') || error.message.includes('403')) {
                userMessage = 'Session expired. Please login again.';
                localStorage.removeItem('authToken');
                sessionStorage.removeItem('authToken');
                setUserLoggedIn(false);
                setTimeout(() => navigate('/login'), 2000);
            }

            showSnackbar(userMessage, severity);
        } finally {
            setIsSubmitting(false);
        }
    };

    // Handle file selection with preview management
    const handleFileSelect = (event) => {
        if (!userLoggedIn) {
            showSnackbar('Please login to upload media', 'warning');
            return;
        }

        const files = Array.from(event.target.files);

        const validFiles = files.filter(file => {
            const fileType = file.type.split('/')[0];
            const isValidType = ['image', 'video'].includes(fileType);
            const isValidSize = file.size <= 10 * 1024 * 1024;

            if (!isValidType) {
                showSnackbar(`Invalid file type: ${file.name}`, 'warning');
                return false;
            }
            if (!isValidSize) {
                showSnackbar(`File too large: ${file.name} (max 10MB)`, 'warning');
                return false;
            }

            return true;
        });

        if (validFiles.length > 0) {
            const newPreviews = validFiles.map(file => URL.createObjectURL(file));
            setMediaFiles(prev => [...prev, ...validFiles]);
            setMediaPreviews(prev => [...prev, ...newPreviews]);
            showSnackbar(`Added ${validFiles.length} file(s)`, 'success');
        }

        event.target.value = '';
    };

    // Remove media file with cleanup
    const removeMediaFile = (index) => {
        // Revoke the preview URL to free memory
        URL.revokeObjectURL(mediaPreviews[index]);
        setMediaFiles(prev => prev.filter((_, i) => i !== index));
        setMediaPreviews(prev => prev.filter((_, i) => i !== index));
        showSnackbar('File removed', 'info');
    };

    // Handle login redirect
    const handleLoginRedirect = () => {
        navigate('/login', { state: { from: location.pathname + location.search } });
    };

    // Close snackbar
    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    // Handle image error - prevent infinite loop
    const handleImageError = (e) => {
        if (!e.target.dataset.fallback) {
            e.target.dataset.fallback = 'true';
            e.target.src = FALLBACK_IMAGE;
        }
    };

    return (
        <Box sx={{ width: "100%" }}>
            <Box sx={{ marginBottom: "0%" }}><Notes /></Box>

            {/* Network Status */}
            {!networkStatus && (
                <Box sx={{
                    position: 'fixed', top: 0, left: 0, right: 0,
                    backgroundColor: '#f59e0b', color: 'white', textAlign: 'center',
                    padding: '8px', zIndex: 9999,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1
                }}>
                    <WifiOff fontSize="small" />
                    <Typography variant="body2">
                        You are offline. Please check your internet connection.
                    </Typography>
                </Box>
            )}

            {/* Header */}
            <Box sx={{ position: "relative" }}>
                <Box sx={{
                    position: "absolute", top: "10%", left: "5%", margin: 0, color: "white",
                    fontSize: "15px", backgroundColor: "rgba(0, 0, 0, 0.5)", padding: "5px 10px", borderRadius: "5px",
                }}>
                    <Typography component="span" sx={{ opacity: 0.5, marginRight: "5px" }}>
                        <Link href="/" style={{ fontSize: "13px", color: "white", textDecoration: "none" }}>
                            Home/
                        </Link>
                    </Typography>
                    <Link href="/order-history" style={{ fontSize: "13px", color: "white", textDecoration: "none" }}>
                        Order History
                    </Link>
                    / Product Review
                </Box>

                <img
                    src="/images/wishlist.png"
                    alt="Order History Background"
                    style={{ width: "100%", height: "50vh", objectFit: "cover" }}
                />

                <Box sx={{
                    position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
                    textShadow: "2px 2px 8px rgba(0, 0, 0, 0.7)",
                    fontSize: { xs: "20px", sm: "24px", md: "28px", lg: "30px" },
                    fontFamily: "Roboto", textAlign: "center", width: "100%", color: "#EBEBEB",
                }}>
                    <b>Product Review</b>
                    <br />
                    {!userLoggedIn ? (
                        <Button
                            variant="contained"
                            onClick={handleLoginRedirect}
                            sx={{
                                backgroundColor: "#EB2329", color: "white", mt: 2,
                                '&:hover': { backgroundColor: "#d9191f" }
                            }}
                        >
                            Login to Review
                        </Button>
                    ) : (
                        <Typography variant="h6" sx={{ mt: 2 }}>
                            {orderDetails ? `Reviewing: ${orderDetails.carBrandName} ${orderDetails.carModelName}` : 'Select an order to review'}
                        </Typography>
                    )}
                </Box>
            </Box>

            {/* Hidden file input */}
            <input
                type="file"
                id="media-file-input"
                multiple
                accept="image/*,video/*"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
            />

            <ThemeProvider theme={theme}>
                <Box sx={{ minHeight: '80vh', py: 4 }}>
                    <Container maxWidth="xl" sx={{
                        width: "90%", backgroundColor: "#FFFFFF", border: "2px solid #EBEBEB", borderRadius: "10px",
                        marginTop: "-75px", marginBottom: "100px", position: "relative", zIndex: 1, minHeight: "400px", p: { xs: 2, md: 4 },
                    }}>
                        {/* Login Alert */}
                        {!userLoggedIn ? (
                            <Alert severity="warning" sx={{ mb: 3 }}>
                                Please login to submit a review.
                            </Alert>
                        ) : loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                <CircularProgress />
                            </Box>
                        ) : !orderDetails ? (
                            <Alert severity="info" sx={{ mb: 3 }}>
                                Please select an order to review.
                            </Alert>
                        ) : (
                            <>
                                {/* Car Details Section */}
                                <Card sx={{ mb: 4 }}>
                                    <CardContent sx={{ p: 4 }}>
                                        {/* Order Info Banner */}
                                        <Paper sx={{
                                            p: 2, mb: 3,
                                            background: 'linear-gradient(45deg, #eb252cff, #eb252cff)',
                                            color: 'white', borderRadius: 2
                                        }}>
                                            <Grid container spacing={2} alignItems="center">
                                                <Grid item xs={12} md={6}>
                                                    <Stack direction="row" spacing={2} alignItems="center">
                                                        <Chip
                                                            label={orderDetails.isOrderPlaced ? "Order Placed" : "Pending"}
                                                            size="small"
                                                            color={orderDetails.isOrderPlaced ? "success" : "warning"}
                                                            sx={{ color: 'white', fontWeight: 'bold' }}
                                                        />
                                                        <Chip
                                                            label={orderDetails.shippingOption || "CIF"}
                                                            size="small"
                                                            variant="outlined"
                                                            sx={{ color: 'white', borderColor: 'white' }}
                                                        />
                                                    </Stack>
                                                </Grid>
                                            </Grid>
                                        </Paper>

                                        {/* Car Image and Details */}
                                        <Grid container spacing={3}>
                                            {/* Single Car Image */}
                                            <Grid item xs={12} md={6}>
                                                <Box sx={{ position: 'relative' }}>
                                                    <Box
                                                        sx={{
                                                            width: '100%',
                                                            borderRadius: 2,
                                                            overflow: 'hidden',
                                                            border: '1px solid #e0e0e0',
                                                            cursor: 'pointer',
                                                            '&:hover': {
                                                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                                            }
                                                        }}
                                                        onClick={() => navigate(`/product/${carModelId}`)}
                                                    >
                                                        <img
                                                            src={carImage}
                                                            alt={`${carDetails?.brand} ${carDetails?.model}`}
                                                            style={{
                                                                width: '100%',
                                                                height: 'auto',
                                                                display: 'block'
                                                            }}
                                                            onError={handleImageError}
                                                        />
                                                    </Box>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            mt: 1, display: 'block', textAlign: 'center',
                                                            color: 'primary.main', cursor: 'pointer',
                                                            '&:hover': { textDecoration: 'underline' }
                                                        }}
                                                        onClick={() => navigate(`/product/${carModelId}`)}
                                                    >
                                                        Click image to view all photos →
                                                    </Typography>
                                                </Box>
                                            </Grid>

                                            {/* Car Details Section */}
                                            <Grid item xs={12} md={6}>
                                                <Box sx={{ pl: { md: 2 } }}>
                                                    <Typography variant="h4" gutterBottom sx={{ color: 'red', fontWeight: 'bold' }}>
                                                        {carDetails?.carName}
                                                    </Typography>

                                                    <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                                                        {carDetails?.brand} • {carDetails?.model} • {carDetails?.carCategoriesName}
                                                    </Typography>

                                                    <Box sx={{ mb: 3 }}>
                                                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                                            {orderDetails?.ourPick && (
                                                                <Chip
                                                                    icon={<CheckCircle />}
                                                                    label="Our Pick"
                                                                    size="small"
                                                                    color="success"
                                                                    sx={{ mb: 1 }}
                                                                />
                                                            )}
                                                            {orderDetails?.specialDeal && (
                                                                <Chip
                                                                    icon={<AttachMoney />}
                                                                    label="Special Deal"
                                                                    size="small"
                                                                    color="warning"
                                                                    sx={{ mb: 1 }}
                                                                />
                                                            )}
                                                            <Chip
                                                                label={`Ref: ${orderDetails?.carRefNumber || 'N/A'}`}
                                                                size="small"
                                                                sx={{ mb: 1 }}
                                                            />
                                                            <Chip
                                                                label={`Chassis: ${orderDetails?.carChassisNumber || 'N/A'}`}
                                                                size="small"
                                                                sx={{ mb: 1 }}
                                                            />
                                                        </Stack>
                                                    </Box>

                                                    <Divider sx={{ my: 2 }} />

                                                    {/* Detailed Specifications Grid */}
                                                    <Grid container spacing={2} sx={{ mb: 3 }}>
                                                        <Grid item xs={6} sm={4}>
                                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                                <LocalGasStation sx={{ color: 'text.secondary', fontSize: '1rem' }} />
                                                                <Typography variant="body2">
                                                                    <strong>Fuel:</strong><br />
                                                                    {carDetails?.fuelType}
                                                                </Typography>
                                                            </Stack>
                                                        </Grid>
                                                        <Grid item xs={6} sm={4}>
                                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                                <Speed sx={{ color: 'text.secondary', fontSize: '1rem' }} />
                                                                <Typography variant="body2">
                                                                    <strong>Trans:</strong><br />
                                                                    {carDetails?.transmission}
                                                                </Typography>
                                                            </Stack>
                                                        </Grid>
                                                        <Grid item xs={6} sm={4}>
                                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                                <People sx={{ color: 'text.secondary', fontSize: '1rem' }} />
                                                                <Typography variant="body2">
                                                                    <strong>Seats:</strong><br />
                                                                    {carDetails?.seatingCapacity}
                                                                </Typography>
                                                            </Stack>
                                                        </Grid>
                                                        <Grid item xs={6} sm={4}>
                                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                                <Build sx={{ color: 'text.secondary', fontSize: '1rem' }} />
                                                                <Typography variant="body2">
                                                                    <strong>Engine:</strong><br />
                                                                    {carDetails?.engine}
                                                                </Typography>
                                                            </Stack>
                                                        </Grid>
                                                        <Grid item xs={6} sm={4}>
                                                            <Typography variant="body2">
                                                                <strong>Mileage:</strong><br />
                                                                {carDetails?.mileage}
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs={6} sm={4}>
                                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                                <ColorLens sx={{ color: 'text.secondary', fontSize: '1rem' }} />
                                                                <Typography variant="body2">
                                                                    <strong>Color:</strong><br />
                                                                    {carDetails?.color}
                                                                </Typography>
                                                            </Stack>
                                                        </Grid>
                                                        <Grid item xs={6} sm={4}>
                                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                                <Directions sx={{ color: 'text.secondary', fontSize: '1rem' }} />
                                                                <Typography variant="body2">
                                                                    <strong>Drive:</strong><br />
                                                                    {carDetails?.carDrive}
                                                                </Typography>
                                                            </Stack>
                                                        </Grid>
                                                        <Grid item xs={6} sm={4}>
                                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                                <CalendarToday sx={{ color: 'text.secondary', fontSize: '1rem' }} />
                                                                <Typography variant="body2">
                                                                    <strong>Reg. Date:</strong><br />
                                                                    {carDetails?.carRegistrationDate || 'N/A'}
                                                                </Typography>
                                                            </Stack>
                                                        </Grid>
                                                        <Grid item xs={6} sm={4}>
                                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                                <LocationOn sx={{ color: 'text.secondary', fontSize: '1rem' }} />
                                                                <Typography variant="body2">
                                                                    <strong>Location:</strong><br />
                                                                    {carDetails?.carLocation}
                                                                </Typography>
                                                            </Stack>
                                                        </Grid>
                                                    </Grid>
                                                </Box>
                                            </Grid>
                                        </Grid>

                                        <Divider sx={{ my: 4 }} />

                                        {/* Rating Section */}
                                        <Box sx={{ mt: 4, mb: 4, p: 3, backgroundColor: '#f8fafc', borderRadius: 2 }}>
                                            <Typography variant="h6" gutterBottom sx={{ mb: 3, color: 'red', textAlign: 'center' }}>
                                                How would you rate this vehicle?
                                            </Typography>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                                                <Rating
                                                    value={rating}
                                                    onChange={(_, newValue) => setRating(newValue)}
                                                    size="large"
                                                    icon={<Star sx={{ fontSize: '3rem', color: '#f59e0b' }} />}
                                                    emptyIcon={<StarBorder sx={{ fontSize: '3rem', color: '#f59e0b' }} />}
                                                    disabled={isSubmitting}
                                                />
                                                <Typography variant="body2" color="text.secondary">
                                                    {rating} out of 5 stars
                                                </Typography>
                                            </Box>
                                        </Box>

                                        {/* Title Field */}
                                        <Box sx={{ mb: 3 }}>
                                            <Typography variant="h6" gutterBottom>Review Title</Typography>
                                            <TextField
                                                fullWidth
                                                placeholder="Give your review a title (e.g., 'Amazing driving experience!')"
                                                value={reviewTitle}
                                                onChange={(e) => setReviewTitle(e.target.value)}
                                                disabled={isSubmitting}
                                                size="small"
                                            />
                                        </Box>

                                        {/* Review Field */}
                                        <Box sx={{ mb: 2 }}>
                                            <Typography variant="h6" gutterBottom>Share your experience</Typography>
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={4}
                                                placeholder="Tell us about your driving experience, performance, comfort, features, and any issues you encountered..."
                                                value={review}
                                                onChange={(e) => setReview(e.target.value)}
                                                disabled={isSubmitting}
                                                size="small"
                                            />
                                        </Box>

                                        {/* Media Upload */}
                                        <Box sx={{ mb: 4 }}>
                                            <Typography variant="h6" gutterBottom>Upload your photos or videos (Optional)</Typography>
                                            <Paper
                                                onClick={() => {
                                                    if (isSubmitting) return;
                                                    document.getElementById('media-file-input').click();
                                                }}
                                                sx={{
                                                    p: 3, mb: 2, textAlign: 'center',
                                                    cursor: isSubmitting ? 'default' : 'pointer',
                                                    background: '#f8fafc',
                                                    border: '2px dashed #cbd5e1', borderRadius: "8px",
                                                    '&:hover': !isSubmitting ? {
                                                        borderColor: '#2563eb',
                                                        backgroundColor: '#f0f9ff'
                                                    } : {}
                                                }}
                                            >
                                                <Stack spacing={2} alignItems="center">
                                                    <Box sx={{ display: 'flex', gap: 2 }}>
                                                        <IconButton
                                                            size="large"
                                                            disabled={isSubmitting}
                                                            sx={{
                                                                background: '#2563eb', color: 'white',
                                                                '&:hover': !isSubmitting ? { background: '#1d4ed8' } : {}
                                                            }}
                                                        >
                                                            <PhotoCamera />
                                                        </IconButton>
                                                        <IconButton
                                                            size="large"
                                                            disabled={isSubmitting}
                                                            sx={{
                                                                background: '#2563eb', color: 'white',
                                                                '&:hover': !isSubmitting ? { background: '#1d4ed8' } : {}
                                                            }}
                                                        >
                                                            <VideoCall />
                                                        </IconButton>
                                                    </Box>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {mediaFiles.length > 0 ? `${mediaFiles.length} file(s) selected` :
                                                            'Click to upload photos or videos of your vehicle'}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Supported: Images and videos up to 10MB
                                                    </Typography>
                                                </Stack>
                                            </Paper>

                                            {mediaFiles.length > 0 && (
                                                <Box sx={{ mt: 2 }}>
                                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                                        Selected Files ({mediaFiles.length})
                                                    </Typography>
                                                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                                        {mediaFiles.map((file, index) => (
                                                            <Box key={index} sx={{ position: 'relative', mb: 2 }}>
                                                                {file.type.startsWith('image/') ? (
                                                                    <img
                                                                        src={mediaPreviews[index]}
                                                                        alt={`Preview ${index + 1}`}
                                                                        style={{
                                                                            width: '100px', height: '80px',
                                                                            objectFit: 'cover', borderRadius: '8px',
                                                                            border: '1px solid #e2e8f0'
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    <Box sx={{
                                                                        width: '100px', height: '80px',
                                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                        background: '#e2e8f0', borderRadius: '8px',
                                                                        border: '1px solid #cbd5e1'
                                                                    }}>
                                                                        <VideoCall sx={{ color: '#64748b', fontSize: '2rem' }} />
                                                                    </Box>
                                                                )}
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => removeMediaFile(index)}
                                                                    disabled={isSubmitting}
                                                                    sx={{
                                                                        position: 'absolute', top: -8, right: -8,
                                                                        background: 'white', boxShadow: 1,
                                                                        '&:hover': { background: '#f1f5f9' }
                                                                    }}
                                                                >
                                                                    <Close fontSize="small" />
                                                                </IconButton>
                                                                <Typography variant="caption" sx={{ display: 'block', mt: 0.5, textAlign: 'center' }}>
                                                                    {file.name.length > 15 ? `${file.name.substring(0, 12)}...` : file.name}
                                                                </Typography>
                                                            </Box>
                                                        ))}
                                                    </Stack>
                                                </Box>
                                            )}
                                        </Box>

                                        {/* Submit Button */}
                                        <Box sx={{ display: 'flex', justifyContent: 'center', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                                            <Button
                                                variant="contained"
                                                size="large"
                                                endIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <Send />}
                                                onClick={submitRating}
                                                disabled={isSubmitting || !rating || !review.trim() || !reviewTitle.trim()}
                                                sx={{
                                                    minWidth: 200,
                                                    background: isSubmitting ? '#94a3b8' : '#ef4444',
                                                    '&:hover': { background: isSubmitting ? '#94a3b8' : '#dc2626' },
                                                    '&:disabled': { background: '#ccc' },
                                                }}
                                            >
                                                {isSubmitting ? 'Submitting Review...' :
                                                    submitted ? 'Submitted!' : 'Submit Review'}
                                            </Button>
                                        </Box>

                                        {submitted && (
                                            <Box sx={{ mt: 3, textAlign: 'center' }}>
                                                <Typography variant="body2" color="success.main" sx={{ fontWeight: 600 }}>
                                                    Thank you for your review! Your feedback is pending approval.
                                                </Typography>
                                            </Box>
                                        )}
                                    </CardContent>
                                </Card>
                            </>
                        )}
                    </Container>
                </Box>
            </ThemeProvider>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                    variant="filled"
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
            <FooterManual />
        </Box>
    );
}